import {
  AreaType,
  AtGradeExpectedIntxTypes,
  AtGradeExpectedIntxTypesTwoLaneRural,
  CrashSeverity,
  SafetyIntxType,
  TwoLaneRuralIntxTypes,
} from "./SPFConstants";
import {
  RoundaboutSpfs,
  get3LegRoundaboutSPFs,
  get4LegRoundaboutSPFs,
  getExpectedCrashes,
  getFatalInjuryCrashRatio,
} from "./spfHelpers";
const getSPFsForIntxType = (intxType: SafetyIntxType) => {
  switch (intxType) {
    case SafetyIntxType.THREE_LEG_MINOR_STOP:
      return {
        getTotalPredicted: ({
          majorRoad,
          minorRoad,
        }: TwoLaneRuralIntxTypes): number =>
          Math.exp(-6.337) *
          Math.pow(majorRoad.aadt, 0.479) *
          Math.pow(minorRoad.aadt, 0.362) *
          Math.exp(-0.33 * Number(majorRoad.hasExclusiveLeft)) *
          Math.exp(0.507 * Number(majorRoad.hasExclusiveRight)),
        getFatalInjuryPredicted: ({
          majorRoad,
          minorRoad,
        }: TwoLaneRuralIntxTypes): number =>
          Math.exp(-6.457) *
          Math.pow(majorRoad.aadt, 0.439) *
          Math.pow(minorRoad.aadt, 0.343) *
          Math.exp(-0.267 * Number(majorRoad.hasExclusiveLeft)) *
          Math.exp(0.56 * Number(majorRoad.hasExclusiveRight)),
      };
    case SafetyIntxType.THREE_LEG_SIGNAL:
      return {
        getTotalPredicted: ({
          majorRoad,
          minorRoad,
        }: TwoLaneRuralIntxTypes): number =>
          Math.exp(-6.813) *
          Math.pow(majorRoad.aadt, 0.451) *
          Math.pow(minorRoad.aadt, 0.349) *
          Math.exp(0.02 * majorRoad.speedLimit) *
          Math.exp(-0.433 * Number(majorRoad.hasCrosswalk)),
        getFatalInjuryPredicted: ({
          majorRoad,
          minorRoad,
        }: TwoLaneRuralIntxTypes): number =>
          Math.exp(-6.981) *
          Math.pow(majorRoad.aadt, 0.452) *
          Math.pow(minorRoad.aadt, 0.287) *
          Math.exp(0.026 * majorRoad.speedLimit) *
          Math.exp(-0.605 * Number(majorRoad.hasCrosswalk)) *
          Math.exp(-0.413 * Number(minorRoad.hasCrosswalk)),
      };
    case SafetyIntxType.FOUR_LEG_MINOR_STOP:
      return {
        getTotalPredicted: ({
          majorRoad,
          minorRoad,
          skewAngle,
        }: TwoLaneRuralIntxTypes): number =>
          Math.exp(-6.359) *
          Math.pow(majorRoad.aadt, 0.528) *
          Math.pow(minorRoad.aadt, 0.275) *
          Math.exp(0.007 * skewAngle),
        getFatalInjuryPredicted: ({
          majorRoad,
          minorRoad,
          skewAngle,
        }: TwoLaneRuralIntxTypes): number =>
          Math.exp(-6.156) *
          Math.pow(majorRoad.aadt, 0.512) *
          Math.pow(minorRoad.aadt, 0.176) *
          Math.exp(0.008 * skewAngle),
      };
    case SafetyIntxType.THREE_LEG_ALL_STOP:
    case SafetyIntxType.FOUR_LEG_ALL_STOP:
      return {
        getTotalPredicted: ({
          majorRoad,
          minorRoad,
        }: TwoLaneRuralIntxTypes): number =>
          Math.exp(-6.581) *
          Math.pow(majorRoad.aadt, 0.68) *
          Math.pow(minorRoad.aadt, 0.064) *
          Math.exp(0.028 * Number(majorRoad.speedLimit)),
        getFatalInjuryPredicted: ({
          majorRoad,
          minorRoad,
        }: TwoLaneRuralIntxTypes): number =>
          Math.exp(-7.541) *
          Math.pow(majorRoad.aadt, 0.639) *
          Math.pow(minorRoad.aadt, 0.134) *
          Math.exp(0.029 * Number(majorRoad.speedLimit)),
      };
    case SafetyIntxType.FOUR_LEG_SIGNAL:
      return {
        getTotalPredicted: ({
          majorRoad,
          minorRoad,
        }: TwoLaneRuralIntxTypes): number =>
          Math.exp(-5.353) *
          Math.pow(majorRoad.aadt, 0.313) *
          Math.pow(minorRoad.aadt, 0.25) *
          Math.exp(0.025 * Number(majorRoad.speedLimit)) *
          Math.exp(0.014 * Number(minorRoad.speedLimit)) *
          Math.exp(0.216 * Number(majorRoad.hasExclusiveRight)),
        getFatalInjuryPredicted: ({
          majorRoad,
          minorRoad,
        }: TwoLaneRuralIntxTypes): number =>
          Math.exp(-4.96) *
          Math.pow(majorRoad.aadt, 0.202) *
          Math.pow(minorRoad.aadt, 0.209) *
          Math.exp(0.028 * Number(majorRoad.speedLimit)) *
          Math.exp(0.018 * Number(minorRoad.speedLimit)) *
          Math.exp(0.388 * Number(majorRoad.hasExclusiveRight)),
      };
    case SafetyIntxType.THREE_LEG_ROUNDABOUT:
      return get3LegRoundaboutSPFs(AreaType.RURAL);
    case SafetyIntxType.FOUR_LEG_ROUNDABOUT:
      return get4LegRoundaboutSPFs(AreaType.RURAL);
    default:
      throw new Error(`Could not find SPFs for intersection type: ${intxType}`);
  }
};

const getFatalInjuryPredictedInitial = (props: TwoLaneRuralIntxTypes) => {
  return getSPFsForIntxType(props.intxType)?.getFatalInjuryPredicted(props);
};
const getPdoPredictedInitial = (props: TwoLaneRuralIntxTypes) => {
  const { getPDOPredicted } = getSPFsForIntxType(
    props.intxType
  ) as RoundaboutSpfs;
  if (!getPDOPredicted) {
    throw new Error("Could not find getPDOPredicted function");
  }
  return getPDOPredicted(props);
};

export const getFatalInjuryPredicted = (
  props: TwoLaneRuralIntxTypes
): number => {
  if (
    props.intxType === SafetyIntxType.THREE_LEG_ROUNDABOUT ||
    props.intxType === SafetyIntxType.FOUR_LEG_ROUNDABOUT
  ) {
    const fiInitial = getFatalInjuryPredictedInitial(props);
    const totalPredicted = getTotalPredicted(props);
    const pdoInitial = getPdoPredictedInitial(props);

    return getFatalInjuryCrashRatio({
      fiInitial,
      pdoInitial,
      totalCrashes: totalPredicted,
    });
  }
  return getFatalInjuryPredictedInitial(props);
};
export const getTotalPredicted = (props: TwoLaneRuralIntxTypes): number => {
  return getSPFsForIntxType(props.intxType)?.getTotalPredicted(props);
};

export const getTotalExpected = (props: AtGradeExpectedIntxTypes): number =>
  getExpectedCrashes({
    ...props,
    crashSeverity: CrashSeverity.TOTAL,
    getPredictedCrashes: getTotalPredicted,
  });

export const getFatalInjuryExpected = (
  props: AtGradeExpectedIntxTypesTwoLaneRural
): number => {
  if (
    props.intxType === SafetyIntxType.THREE_LEG_ROUNDABOUT ||
    props.intxType === SafetyIntxType.FOUR_LEG_ROUNDABOUT
  ) {
    const fiInitial = getFatalInjuryPredictedInitial(props);
    const totalExpected = getTotalExpected(props);
    const pdoInitial = getPdoPredictedInitial(props);

    return getFatalInjuryCrashRatio({
      fiInitial,
      pdoInitial,
      totalCrashes: totalExpected,
    });
  }
  return getExpectedCrashes({
    ...props,
    crashSeverity: CrashSeverity.FATAL_INJURY,
    getPredictedCrashes: getFatalInjuryPredicted,
  });
};
